import {
  alarmsAvailable,
  cancelAllAlarms,
  onAlarmAction,
  syncAlarms,
  type AlarmMode,
} from './alarms.js';
import { isNativeShell } from './platform.js';
import { nativeBridge } from './secretarioNative.js';

/**
 * Avisos no aparelho. No app instalado, lembretes e compromissos são agendados
 * no próprio aparelho (chegam com o app fechado) e mensagem nova avisa enquanto
 * o app está em segundo plano. O aviso de mensagem nunca leva o texto.
 */

export type NotifyPrefs = { messages: boolean; reminders: boolean; secretary: boolean };
export type PlannedNotice = {
  key: string;
  title: string;
  body: string;
  at: Date;
  route: string;
  /** Só no Android: "alarm" toca como despertador. O padrão é o aviso comum. */
  mode?: AlarmMode;
  /** Só no Android: ler o aviso em voz alta. */
  speak?: boolean;
};
/** O que ficou agendado no aparelho e se a hora é garantida (alarme exato do Android). */
export type ScheduledResult = { exact: boolean; keys: string[] };
const NOTHING_SCHEDULED: ScheduledResult = { exact: false, keys: [] };
export type NotifyPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';
export type IncomingMessage = { roomId: string; title: string };

const REMINDERS_CHANNEL = 'lembretes';
const MESSAGES_CHANNEL = 'mensagens';
// Os avisos agendados ficam na primeira metade dos ids; os de mensagem, na segunda.
const SCHEDULED_ID_LIMIT = 1_000_000_000;
// O Android limita os alarmes por app; os mais próximos bastam (a lista é refeita a cada abertura).
const MAX_SCHEDULED = 48;
// No Android os lembretes passaram ao alarme do sistema; o que o agendador
// antigo ainda guardava é retirado uma vez só, para nada avisar em dobro.
const LEGACY_RETIRED_KEY = 'secretario.alarmes.migrado';

let prefs: NotifyPrefs = { messages: true, reminders: true, secretary: true };
// Com o push ligado, o aviso de mensagem em segundo plano vem do sistema.
let pushActive = false;

export function setPushActive(active: boolean): void {
  pushActive = active;
}
const shownMessageIds = new Set<number>();
let channelsReady: Promise<void> | undefined;
let audio: AudioContext | undefined;

/** O que a pessoa escolheu em Ajustes → Notificações. */
export function setNotifyPrefs(next: NotifyPrefs): void {
  prefs = next;
}

function hash(key: string): number {
  let value = 0;
  for (let index = 0; index < key.length; index += 1) {
    value = (value * 31 + key.charCodeAt(index)) | 0;
  }
  return Math.abs(value) % (SCHEDULED_ID_LIMIT - 1);
}

// O objeto do plugin responde a qualquer nome, inclusive "then": devolvido
// direto por uma função async, a espera tenta chamá-lo e falha no aparelho.
// Por isso ele sai embrulhado.
async function plugin() {
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  return { LocalNotifications };
}

function ensureChannels(): Promise<void> {
  channelsReady ??= (async () => {
    const { LocalNotifications } = await plugin();
    // Canais só existem no Android; no iOS a chamada não se aplica.
    await LocalNotifications.createChannel({
      id: REMINDERS_CHANNEL,
      name: 'Lembretes',
      description: 'Na hora do lembrete e antes dos compromissos',
      importance: 4,
      visibility: 0,
      vibration: true,
    }).catch(() => {});
    await LocalNotifications.createChannel({
      id: MESSAGES_CHANNEL,
      name: 'Mensagens',
      description: 'Quando chega mensagem nova',
      importance: 4,
      visibility: 0,
      vibration: true,
    }).catch(() => {});
  })();
  return channelsReady;
}

export async function notifyPermission(): Promise<NotifyPermission> {
  if (!isNativeShell()) return 'unsupported';
  try {
    const { LocalNotifications } = await plugin();
    const { display } = await LocalNotifications.checkPermissions();
    if (display === 'granted') return 'granted';
    return display === 'denied' ? 'denied' : 'prompt';
  } catch {
    return 'unsupported';
  }
}

/** Abre o pedido do sistema. Só é chamado depois de a pessoa dizer "pode avisar". */
export async function requestNotifyPermission(): Promise<boolean> {
  if (!isNativeShell()) return false;
  try {
    const { LocalNotifications } = await plugin();
    const { display } = await LocalNotifications.requestPermissions();
    if (display !== 'granted') return false;
    await ensureChannels();
    return true;
  } catch {
    return false;
  }
}

function legacyRetired(): boolean {
  try {
    return window.localStorage.getItem(LEGACY_RETIRED_KEY) === '1';
  } catch {
    return false;
  }
}

function setLegacyRetired(done: boolean): void {
  try {
    if (done) window.localStorage.setItem(LEGACY_RETIRED_KEY, '1');
    else window.localStorage.removeItem(LEGACY_RETIRED_KEY);
  } catch {
    /* sem armazenamento: a limpeza repete na próxima abertura, sem prejuízo */
  }
}

/** Uma vez só: tira do agendador antigo os lembretes que o alarme do sistema assumiu. */
async function retireLegacySchedules(): Promise<void> {
  if (legacyRetired()) return;
  try {
    const { LocalNotifications } = await plugin();
    const { notifications: pending } = await LocalNotifications.getPending();
    const old = pending.filter((notice) => notice.id <= SCHEDULED_ID_LIMIT);
    if (old.length > 0) {
      await LocalNotifications.cancel({ notifications: old.map(({ id }) => ({ id })) });
    }
    setLegacyRetired(true);
  } catch {
    /* fica para a próxima sincronização */
  }
}

/**
 * Deixa agendado no aparelho exatamente o que está na lista: o que é novo
 * entra, o que mudou é trocado e o que saiu (cancelado, concluído) é retirado.
 */
export async function syncScheduledNotices(planned: PlannedNotice[]): Promise<ScheduledResult> {
  if ((await notifyPermission()) !== 'granted') return NOTHING_SCHEDULED;
  const soon = Date.now() + 5_000;
  const upcoming = prefs.reminders
    ? planned
        .filter((notice) => notice.at.getTime() > soon)
        .sort((a, b) => a.at.getTime() - b.at.getTime())
        .slice(0, MAX_SCHEDULED)
    : [];
  // Android: alarme exato do sistema, que sai na hora com o aparelho em repouso
  // e é refeito depois de reinício.
  if (alarmsAvailable()) {
    const taken = await syncAlarms(
      upcoming.map((notice) => ({
        id: notice.key,
        at: notice.at.getTime(),
        title: notice.title,
        body: notice.body,
        route: notice.route,
        mode: notice.mode ?? 'notice',
        ...(notice.speak ? { speak: true } : {}),
      })),
    );
    if (taken) {
      await retireLegacySchedules();
      return { exact: taken.exact, keys: upcoming.map((notice) => notice.key) };
    }
    // A parte nativa falhou desta vez: segue pelo agendador antigo (e a limpeza volta a valer).
    setLegacyRetired(false);
  }
  const { LocalNotifications } = await plugin();
  await ensureChannels();
  const wanted = new Map<number, PlannedNotice>();
  upcoming.forEach((notice) => wanted.set(hash(notice.key) + 1, notice));
  const { notifications: pending } = await LocalNotifications.getPending();
  const stale = pending.filter(
    (notice) => notice.id <= SCHEDULED_ID_LIMIT && !wanted.has(notice.id),
  );
  if (stale.length > 0) {
    await LocalNotifications.cancel({ notifications: stale.map(({ id }) => ({ id })) });
  }
  if (wanted.size === 0) return NOTHING_SCHEDULED;
  await LocalNotifications.schedule({
    notifications: [...wanted].map(([id, notice]) => ({
      id,
      title: notice.title,
      body: notice.body,
      channelId: REMINDERS_CHANNEL,
      schedule: { at: notice.at, allowWhileIdle: true },
      extra: { route: notice.route },
    })),
  });
  // Agendador comum: a hora não é garantida com o aparelho em repouso.
  return { exact: false, keys: upcoming.map((notice) => notice.key) };
}

/** Ao sair da conta neste aparelho: nada da pessoa fica agendado. */
export async function clearAllNotices(): Promise<void> {
  if (!isNativeShell()) return;
  await cancelAllAlarms();
  try {
    const { LocalNotifications } = await plugin();
    const { notifications: pending } = await LocalNotifications.getPending();
    if (pending.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.map(({ id }) => ({ id })) });
    }
    await LocalNotifications.removeAllDeliveredNotifications();
    shownMessageIds.clear();
  } catch {
    /* sem avisos para limpar */
  }
}

/** Toque num aviso leva à tela dele. Devolve como desligar. */
export async function onNoticeTap(open: (route: string) => void): Promise<() => void> {
  if (!isNativeShell()) return () => {};
  const { LocalNotifications } = await plugin();
  const handle = await LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (action) => {
      const route = (action.notification.extra as { route?: unknown } | undefined)?.route;
      if (typeof route === 'string' && route.startsWith('/')) open(route);
    },
  );
  // Android: o lembrete agendado pelo alarme do sistema avisa o toque por outro caminho.
  const stopAlarms = await onAlarmAction(({ route }) => {
    if (route.startsWith('/')) open(route);
  });
  return () => {
    void handle.remove();
    stopAlarms();
  };
}

/** Duas notas curtas e suaves, para mensagem que chega com o app aberto em outra tela. */
function playIncomingTone(): void {
  try {
    const scope = window as unknown as { webkitAudioContext?: typeof AudioContext };
    const Context = window.AudioContext ?? scope.webkitAudioContext;
    if (!Context) return;
    audio ??= new Context();
    const context = audio;
    if (context.state === 'suspended') void context.resume();
    const start = context.currentTime + 0.01;
    for (const [frequency, offset] of [
      [880, 0],
      [1174.66, 0.09],
    ] as const) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.1, start + offset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.22);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start + offset);
      oscillator.stop(start + offset + 0.25);
    }
  } catch {
    /* sem áudio neste aparelho: o selo de não lidas continua valendo */
  }
}

async function showMessageNotice(message: IncomingMessage): Promise<void> {
  if ((await notifyPermission()) !== 'granted') return;
  const { LocalNotifications } = await plugin();
  await ensureChannels();
  // Um aviso por conversa: mensagem nova na mesma conversa atualiza o que já está lá.
  const id = SCHEDULED_ID_LIMIT + 1 + hash(message.roomId);
  shownMessageIds.add(id);
  await LocalNotifications.schedule({
    notifications: [
      {
        id,
        title: message.title,
        body: 'Nova mensagem',
        channelId: MESSAGES_CHANNEL,
        group: MESSAGES_CHANNEL,
        extra: { route: `/conversas/${encodeURIComponent(message.roomId)}` },
      },
    ],
  });
}

const MUTED_KEY = 'secretario.silenciadas';

function mutedRooms(): Set<string> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(MUTED_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch {
    return new Set();
  }
}

/** Esta conversa está sem avisos neste aparelho? */
export function isRoomMuted(roomId: string): boolean {
  return mutedRooms().has(roomId);
}

export function setRoomMuted(roomId: string, muted: boolean): void {
  const rooms = mutedRooms();
  if (muted) rooms.add(roomId);
  else rooms.delete(roomId);
  try {
    window.localStorage.setItem(MUTED_KEY, JSON.stringify([...rooms]));
  } catch {
    /* sem armazenamento: vale até fechar o app */
  }
  void nativeBridge.setMutedRooms([...rooms]);
}

/** Silenciou pelo próprio aviso (ou em outra abertura): junta com o que está aqui. */
export async function syncMutedRooms(): Promise<void> {
  const native = await nativeBridge.mutedRooms();
  if (!native) return;
  const rooms = mutedRooms();
  const before = rooms.size;
  for (const room of native) rooms.add(room);
  if (rooms.size !== before) {
    try {
      window.localStorage.setItem(MUTED_KEY, JSON.stringify([...rooms]));
    } catch {
      /* sem armazenamento */
    }
  }
  if (rooms.size !== native.length) void nativeBridge.setMutedRooms([...rooms]);
}

/** Mensagem de outra pessoa acabou de chegar. */
export function onIncomingMessage(message: IncomingMessage): void {
  if (!prefs.messages || isRoomMuted(message.roomId)) return;
  if (document.visibilityState === 'visible') {
    const here = decodeURIComponent(window.location.pathname);
    if (!here.endsWith(`/conversas/${message.roomId}`)) playIncomingTone();
    return;
  }
  if (isNativeShell() && !pushActive) void showMessageNotice(message).catch(() => {});
}

/** De volta ao app: os avisos de mensagem já cumpriram o papel. */
export async function clearMessageNotices(): Promise<void> {
  if (!isNativeShell() || shownMessageIds.size === 0) return;
  const ids = [...shownMessageIds];
  shownMessageIds.clear();
  try {
    const { LocalNotifications } = await plugin();
    await LocalNotifications.removeDeliveredNotifications({
      notifications: ids.map((id) => ({ id, title: '', body: '' })),
    });
  } catch {
    /* já tinham sido dispensados */
  }
}
